import type {
    CallbackSlotId,
    Service,
    ServiceId,
    VehicleClass,
    VehicleClassId
} from '@/types'

// Source unique des prix : la grille tarifaire, le simulateur et la route
// de demande de rappel lisent tous ces valeurs. Prix TTC, temps barème en heures.

export const VEHICLE_CLASSES: VehicleClass[] = [
    { id: 'citadine', label: 'Citadine', examples: 'Clio, 208, C3, Yaris' },
    { id: 'compacte', label: 'Compacte', examples: '308, Golf, Mégane, Corolla' },
    { id: 'suv', label: 'SUV et familiale', examples: '3008, Captur, Tiguan, Scénic' },
    { id: 'utilitaire', label: 'Utilitaire', examples: 'Kangoo, Berlingo, Trafic' }
]

export const SERVICES: Service[] = [
    {
        id: 'revision',
        name: 'Révision constructeur',
        summary: 'Vidange, filtres huile, air et habitacle, contrôle 40 points, carnet tamponné',
        prices: {
            citadine: { ttc: 149, hours: 1 },
            compacte: { ttc: 179, hours: 1.2 },
            suv: { ttc: 219, hours: 1.4 },
            utilitaire: { ttc: 229, hours: 1.5 }
        }
    },
    {
        id: 'plaquettes',
        name: 'Plaquettes de frein avant',
        summary: 'Plaquettes neuves, contrôle des disques et du liquide, essai routier',
        group: 'freinage',
        prices: {
            citadine: { ttc: 99, hours: 0.8 },
            compacte: { ttc: 119, hours: 0.9 },
            suv: { ttc: 139, hours: 1 },
            utilitaire: { ttc: 149, hours: 1 }
        }
    },
    {
        id: 'disques',
        name: 'Disques et plaquettes avant',
        summary: 'Disques et plaquettes neufs, nettoyage des étriers, essai routier',
        group: 'freinage',
        prices: {
            citadine: { ttc: 229, hours: 1.3 },
            compacte: { ttc: 279, hours: 1.4 },
            suv: { ttc: 339, hours: 1.6 },
            utilitaire: { ttc: 359, hours: 1.6 }
        }
    },
    {
        id: 'distribution',
        name: 'Kit distribution et pompe à eau',
        summary: 'Courroie, galets, pompe à eau et liquide de refroidissement neufs',
        prices: {
            citadine: { ttc: 489, hours: 3.5 },
            compacte: { ttc: 589, hours: 4 },
            suv: { ttc: 689, hours: 4.5 },
            utilitaire: { ttc: 719, hours: 4.5 }
        }
    },
    {
        id: 'pneus',
        name: '2 pneus avant posés',
        summary: 'Pneus de marque, valves neuves, équilibrage et pression contrôlée',
        prices: {
            citadine: { ttc: 179, hours: 0.6 },
            compacte: { ttc: 229, hours: 0.6 },
            suv: { ttc: 299, hours: 0.7 },
            utilitaire: { ttc: 279, hours: 0.7 }
        }
    },
    {
        id: 'climatisation',
        name: 'Recharge de climatisation',
        summary: 'Contrôle d’étanchéité, recharge du gaz, test de température',
        prices: {
            citadine: { ttc: 99, hours: 0.7 },
            compacte: { ttc: 109, hours: 0.7 },
            suv: { ttc: 119, hours: 0.8 },
            utilitaire: { ttc: 119, hours: 0.8 }
        }
    },
    {
        id: 'diagnostic',
        name: 'Diagnostic électronique',
        summary: 'Lecture des codes défaut et explication claire, sans jargon',
        free: true,
        prices: {
            citadine: { ttc: 0, hours: 0.5 },
            compacte: { ttc: 0, hours: 0.5 },
            suv: { ttc: 0, hours: 0.5 },
            utilitaire: { ttc: 0, hours: 0.5 }
        }
    }
]

export const DEFAULT_VEHICLE_CLASS: VehicleClassId = 'compacte'

export const DEFAULT_SERVICES: ServiceId[] = ['revision']

export const CALLBACK_SLOTS: { id: CallbackSlotId, label: string }[] = [
    { id: 'asap', label: 'Dès que possible' },
    { id: 'midi', label: 'Entre 12 h et 14 h' },
    { id: 'soir', label: 'Après 17 h' }
]

// Familles affichées dans la grille tarifaire, reliées aux interventions du simulateur.
export const PRICE_FAMILIES: {
    ref: string
    name: string
    description: string
    includes: string[]
    duration: string
    options: { label: string, serviceId: ServiceId }[]
    estimateId: ServiceId
}[] = [
    {
        ref: '01',
        name: 'Révision constructeur',
        description: 'Le programme d’entretien de votre carnet, à la lettre. Votre garantie constructeur reste intacte.',
        includes: ['Huile préconisée', 'Filtres huile, air, habitacle', 'Contrôle 40 points', 'Carnet tamponné'],
        duration: '1 h 30, rendue le jour même',
        options: [{ label: 'Révision complète', serviceId: 'revision' }],
        estimateId: 'revision'
    },
    {
        ref: '02',
        name: 'Freinage',
        description: 'Plaquettes seules ou disques et plaquettes, toujours remplacés par paire, avec contrôle du liquide de frein.',
        includes: ['Pièces d’origine ou équivalentes', 'Nettoyage des étriers', 'Essai routier'],
        duration: '1 h à 1 h 30',
        options: [
            { label: 'Plaquettes avant', serviceId: 'plaquettes' },
            { label: 'Disques et plaquettes', serviceId: 'disques' }
        ],
        estimateId: 'plaquettes'
    },
    {
        ref: '03',
        name: 'Distribution',
        description: 'Le kit complet et la pompe à eau, changés ensemble pour ne pas démonter deux fois. Sur un moteur à chaîne, on vous le dit au diagnostic.',
        includes: ['Courroie et galets', 'Pompe à eau', 'Liquide de refroidissement'],
        duration: 'Une demi-journée',
        options: [{ label: 'Kit et pompe à eau', serviceId: 'distribution' }],
        estimateId: 'distribution'
    },
    {
        ref: '04',
        name: 'Pneus',
        description: 'Trois gammes au choix, posées sur démonte-pneus sans levier pour ne jamais rayer une jante.',
        includes: ['Valves neuves', 'Équilibrage', 'Pression contrôlée', 'Recyclage des anciens'],
        duration: '45 min',
        options: [{ label: '2 pneus posés', serviceId: 'pneus' }],
        estimateId: 'pneus'
    },
    {
        ref: '05',
        name: 'Climatisation',
        description: 'Recharge au bon gaz pour votre véhicule, après un contrôle d’étanchéité. Pas de recharge à l’aveugle.',
        includes: ['Contrôle d’étanchéité', 'Recharge du gaz', 'Test de température'],
        duration: '45 min',
        options: [{ label: 'Recharge complète', serviceId: 'climatisation' }],
        estimateId: 'climatisation'
    },
    {
        ref: '06',
        name: 'Diagnostic électronique',
        description: 'Voyant allumé, bruit suspect, perte de puissance : on cherche, on vous explique, et vous décidez.',
        includes: ['Valise multimarque', 'Lecture des codes défaut', 'Compte rendu oral'],
        duration: '30 min',
        options: [{ label: 'Diagnostic', serviceId: 'diagnostic' }],
        estimateId: 'diagnostic'
    }
]
